/**
 * 県の画面で、ゲームの出来事を遊び方の記録につなぐ。
 *
 * 記録するのは「プレイの始まりと終わり」だけ（1手ごとの記録はしない）。
 * - 始まり：開始・もう一度・リスタート（コンティニューは同じプレイなので数えない）
 * - 終わり：ゲームオーバー（detail=over）か、終わらずに次を始めた（detail=quit）
 */

import { GameState } from '../core/GameState';
import type { GameContext } from '../game/GameContext';
import { ALL_PREFECTURE_IDS } from '../game/config/regions';
import type { Telemetry } from './telemetry';
import type { TelemetryMode } from './telemetrySchema';

interface OpenRun {
  mode: TelemetryMode;
  score: number;
  level: number;
  startedAt: number;
}

export function attachTelemetryHooks(
  context: GameContext,
  telemetry: Telemetry,
  options: { totalPlaysBefore?: () => number; now?: () => number } = {},
): void {
  const { bus, state } = context;
  // 47都道府県のどれでもない試しのステージ（巣鴨など）は県を付けない（サーバーが受け取らないため）
  const region = ALL_PREFECTURE_IDS.includes(context.stage.regionId) ? context.stage.regionId : undefined;
  const now = options.now ?? (() => Date.now());
  let relaxed = false;
  let run: OpenRun | null = null;
  /** ゲームオーバーになったプレイ（コンティニューしたら続きにする）。 */
  let ended: OpenRun | null = null;

  const modeOf = (): TelemetryMode => (context.daily ? 'daily' : relaxed ? 'relaxed' : 'normal');
  const usage = () => {
    const r = context.assist.runState;
    return { continues: r.continues, items: r.itemsUsed };
  };

  bus.on('mode:change', (p) => {
    relaxed = p.relaxed;
  });
  bus.on('score:change', ({ score }) => {
    if (run) run.score = score;
  });
  bus.on('run:maxLevel', ({ level }) => {
    if (run) run.level = Math.max(run.level, level);
  });

  /** リスタートの直前に控えた、終わらなかったプレイ（点や使った数が 0 に戻る前の値）。 */
  let restarting: (OpenRun & { continues: number; items: number }) | null = null;

  const startRun = () => {
    const prev = restarting ?? (run ? { ...run, ...usage() } : null);
    restarting = null;
    if (prev) {
      // 終わらないまま次を始めた（リスタート）
      telemetry.track('play_end', {
        region,
        mode: prev.mode,
        level: prev.level,
        score: prev.score,
        playMs: Math.max(0, Math.round(now() - prev.startedAt)),
        continues: prev.continues,
        items: prev.items,
        detail: 'quit',
      });
    }
    run = { mode: modeOf(), score: 0, level: 0, startedAt: now() };
    ended = null;
    if (telemetry.markFirstPlay() && (options.totalPlaysBefore?.() ?? 0) === 0) {
      telemetry.track('first_play', { region, mode: run.mode });
    }
    telemetry.track('play_start', { region, mode: run.mode });
  };

  state.onChange((to, from) => {
    if (to !== GameState.PLAYING) return;
    // READY → PLAYING が「新しいプレイ」（もう一度・リスタートも READY を通る）。
    // PAUSED → PLAYING（再開）と GAME_OVER → PLAYING（コンティニュー）は同じプレイ
    if (from === GameState.READY) startRun();
  });
  bus.on('command:restart', () => {
    // リスタートで点が 0 に戻る前に、いまのプレイを控えておく（この手はゲーム本体より先に登録する）。
    // 遊んでいる途中のリスタートは READY を通らないので、ここで区切る
    if (!run) return;
    restarting = { ...run, ...usage() };
    if (state.is(GameState.PLAYING)) {
      queueMicrotask(() => {
        if (restarting && state.is(GameState.PLAYING)) startRun();
      });
    }
  });

  bus.on('game:over', (result) => {
    const mode: TelemetryMode = context.daily ? 'daily' : result.relaxed ? 'relaxed' : 'normal';
    telemetry.track('play_end', {
      region,
      mode,
      level: result.maxLevelReached,
      drops: result.drops,
      playMs: result.playMs,
      score: result.score,
      ...usage(),
      detail: 'over',
    });
    ended = run;
    run = null;
    // 結果画面が出たところでまとめて送る
    void telemetry.flush();
  });
  // コンティニューしたら同じプレイを続ける
  bus.on('run:continued', () => {
    run = ended ?? { mode: modeOf(), score: 0, level: 0, startedAt: now() };
    ended = null;
  });

  bus.on('settings:change', ({ settings }) => {
    if (!settings.sendUsageStats) telemetry.discard();
  });
}
